import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeDollarSign, Download, PackageSearch, Pencil, Plus, Search, Upload, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type {
  ClientAgencyRelationship,
  Organization,
  Product,
  ProductCategory,
  ProductFamily,
  ProductPrice,
  Venue,
} from '../../types/domain';
import { ProductImportPanel } from './ProductImportPanel';

interface ProductDraft {
  client: string;
  family: string;
  name: string;
  variant_label: string;
  sku: string;
  theme_color: string;
  barcode: string;
  packaging_type: string;
  units_per_case: string;
  category: string;
  unit_price: string;
  package_quantity: string;
  measurement_unit: string;
  is_active: boolean;
  image: File | null;
}

const emptyDraft: ProductDraft = {
  client: '',
  family: '',
  name: '',
  variant_label: '',
  sku: '',
  theme_color: '',
  barcode: '',
  packaging_type: '',
  units_per_case: '',
  category: '',
  unit_price: '',
  package_quantity: '',
  measurement_unit: '',
  is_active: true,
  image: null,
};

interface ProductPriceDraft {
  venue: string;
  price_type: 'standard' | 'promotional';
  amount: string;
  starts_on: string;
  ends_on: string;
  is_active: boolean;
}

const emptyPriceDraft: ProductPriceDraft = {
  venue: '', price_type: 'standard', amount: '', starts_on: '', ends_on: '', is_active: true,
};

function generatedVariantLabel({
  package_quantity,
  measurement_unit,
  packaging_type,
}: Pick<ProductDraft, 'package_quantity' | 'measurement_unit' | 'packaging_type'>) {
  if (!package_quantity || !measurement_unit || !packaging_type) return '';
  return `${package_quantity} ${measurement_unit} ${packaging_type}`;
}

function hasReportingDetailsChange(product: Product, nextDraft: ProductDraft) {
  const currentPackageQuantity = product.package_quantity ?? '';
  const packageQuantityChanged = currentPackageQuantity === ''
    ? nextDraft.package_quantity !== ''
    : Number(currentPackageQuantity) !== Number(nextDraft.package_quantity);
  return product.family !== (nextDraft.family || null)
    || packageQuantityChanged
    || product.measurement_unit !== nextDraft.measurement_unit;
}

const priceFormatter = new Intl.NumberFormat('en-PK', {
  style: 'currency',
  currency: 'PKR',
  maximumFractionDigits: 2,
});

function mediaFileName(source: string | null) {
  if (!source) return null;
  try {
    const fileName = new URL(source).pathname.split('/').pop();
    return fileName ? decodeURIComponent(fileName) : null;
  } catch {
    return source.split('?').at(0)?.split('/').pop() ?? null;
  }
}

function ProductImage({ className = '', name, source }: {
  className?: string;
  name: string;
  source: string | null;
}) {
  const [failedSource, setFailedSource] = useState<string | null>(null);

  if (!source || failedSource === source) {
    return (
      <span aria-label={`${name} has no product image`} className={`product-image ${className}`} role="img">
        <PackageSearch aria-hidden="true" size={22} />
      </span>
    );
  }

  return (
    <span className={`product-image ${className}`}>
      <img alt={`${name} product`} onError={() => setFailedSource(source)} src={source} />
    </span>
  );
}

export function ProductsPage() {
  const { accessToken, capabilities, isPlatformAdmin, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const canManage = capabilities.includes('products:manage');
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [draft, setDraft] = useState<ProductDraft>(emptyDraft);
  const [variantLabelIsAutomatic, setVariantLabelIsAutomatic] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pricingProduct, setPricingProduct] = useState<Product | null>(null);
  const [editingPrice, setEditingPrice] = useState<ProductPrice | null>(null);
  const [priceDraft, setPriceDraft] = useState<ProductPriceDraft>(emptyPriceDraft);
  const [reportingDetailsProduct, setReportingDetailsProduct] = useState<Product | null>(null);
  const [reportingDetailsReason, setReportingDetailsReason] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const exportMutation = useMutation({
    mutationFn: () => downloadApiFile(
      `/products/export/${debouncedSearch ? `?search=${encodeURIComponent(debouncedSearch)}` : ''}`,
      accessToken ? { accessToken } : {},
    ),
  });
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);
  const productParams = new URLSearchParams({ page_size: '100', ordering: 'name' });
  if (debouncedSearch) productParams.set('search', debouncedSearch);
  const products = useApiCollection<Product>(
    'products',
    `/products/?${productParams.toString()}`,
    accessToken,
  );
  const families = useApiCollection<ProductFamily>(
    'product-families',
    '/product-families/?page_size=100&ordering=name',
    accessToken,
    canManage,
  );
  const categories = useApiCollection<ProductCategory>(
    'product-categories',
    '/product-categories/?page_size=100&ordering=name',
    accessToken,
    canManage,
  );
  const organizations = useApiCollection<Organization>(
    'product-organizations',
    '/organizations/?page_size=100&ordering=name',
    accessToken,
    canManage,
  );
  const relationships = useApiCollection<ClientAgencyRelationship>(
    'product-relationships',
    '/organization-relationships/?page_size=100&is_active=true',
    accessToken,
    selectedWorkspace?.type === 'agency',
  );
  const venues = useApiCollection<Venue>(
    'product-price-venues',
    '/venues/?page_size=100&ordering=name&is_active=true',
    accessToken,
    Boolean(pricingProduct),
  );
  const prices = useApiCollection<ProductPrice>(
    'product-prices',
    `/product-prices/?page_size=100&ordering=-starts_on${pricingProduct ? `&product=${pricingProduct.id}` : ''}`,
    accessToken,
    Boolean(pricingProduct),
  );
  const clientOptions = (organizations.data?.results ?? []).filter((organization) => (
    organization.type === 'client'
    && (isPlatformAdmin || organization.id === selectedWorkspace?.organizationId)
  ));
  const rows = useMemo(() => {
    const allProducts = products.data?.results ?? [];
    const organizationId = selectedWorkspace?.organizationId;
    if (!organizationId) return allProducts;
    if (selectedWorkspace?.type === 'client') {
      return allProducts.filter((product) => product.client === organizationId);
    }
    if (selectedWorkspace?.type === 'agency') {
      const clientIds = new Set((relationships.data?.results ?? [])
        .filter((relationship) => relationship.agency === organizationId && relationship.is_active)
        .map((relationship) => relationship.client));
      return allProducts.filter((product) => clientIds.has(product.client));
    }
    return allProducts;
  }, [products.data?.results, relationships.data?.results, selectedWorkspace]);
  const storedImage = editing && editing !== 'new' ? editing.image : null;
  const displayedImage = imagePreview ?? storedImage;
  const familyOptions = (families.data?.results ?? []).filter((family) => (
    family.client === draft.client
    && (family.is_active || family.id === draft.family)
  ));
  const categoryOptions = (categories.data?.results ?? []).filter((category) => (
    category.is_active || category.id === draft.category
  ));

  useEffect(() => () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const mutation = useMutation({
    mutationFn: () => {
      const creating = editing === 'new';
      const body = new FormData();
      if (creating) body.append('client', draft.client);
      body.append('family', draft.family);
      body.append('name', draft.name);
      body.append('variant_label', draft.variant_label);
      body.append('sku', draft.sku);
      body.append('theme_color', draft.theme_color.trim());
      body.append('barcode', draft.barcode);
      body.append('packaging_type', draft.packaging_type);
      body.append('units_per_case', draft.units_per_case);
      body.append('category', draft.category);
      body.append('is_active', String(draft.is_active));
      if (draft.unit_price) body.append('unit_price', draft.unit_price);
      body.append('package_quantity', draft.package_quantity);
      body.append('measurement_unit', draft.measurement_unit);
      if (draft.image) body.append('image', draft.image);
      return apiRequest<Product>(
        creating ? '/products/' : `/products/${editing?.id}/`,
        {
          ...(accessToken ? { accessToken } : {}),
          method: creating ? 'POST' : 'PATCH',
          body,
        },
      );
    },
    onSuccess: async () => {
      setEditing(null);
      setDraft(emptyDraft);
      setImagePreview(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'products'] }),
        queryClient.invalidateQueries({ queryKey: ['overview'] }),
        queryClient.invalidateQueries({ queryKey: ['overview-store-detail'] }),
        queryClient.invalidateQueries({ queryKey: ['overview-product-detail'] }),
      ]);
    },
  });

  const reportingDetailsMutation = useMutation({
    mutationFn: (scope: 'historical' | 'future') => apiRequest<Product>(
      `/products/${reportingDetailsProduct?.id}/apply-reporting-details/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          family: draft.family || null,
          package_quantity: draft.package_quantity || null,
          measurement_unit: draft.measurement_unit,
          reason: reportingDetailsReason,
          scope,
        }),
      },
    ),
    onSuccess: async () => {
      setReportingDetailsProduct(null);
      setReportingDetailsReason('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'products'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'product-families'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-products'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-product-family-targets'] }),
        queryClient.invalidateQueries({ queryKey: ['overview'] }),
      ]);
      mutation.mutate();
    },
  });

  function beginCreate() {
    mutation.reset();
    setImagePreview(null);
    setVariantLabelIsAutomatic(true);
    setDraft({
      ...emptyDraft,
      client: selectedWorkspace?.type === 'client'
        ? selectedWorkspace.organizationId ?? ''
        : clientOptions[0]?.id ?? '',
    });
    setEditing('new');
  }

  function beginEdit(product: Product) {
    mutation.reset();
    setImagePreview(null);
    const productDraft = {
      client: product.client,
      family: product.family ?? '',
      name: product.name,
      variant_label: product.variant_label ?? '',
      sku: product.sku,
      theme_color: product.theme_color ?? '',
      barcode: product.barcode,
      packaging_type: product.packaging_type,
      units_per_case: product.units_per_case?.toString() ?? '',
      category: product.category ?? '',
      unit_price: product.unit_price ?? '',
      package_quantity: product.package_quantity ?? '',
      measurement_unit: product.measurement_unit ?? '',
      is_active: product.is_active,
      image: null,
    };
    setVariantLabelIsAutomatic(product.variant_label === generatedVariantLabel(productDraft));
    setDraft(productDraft);
    setEditing(product);
  }

  function closeForm() {
    if (mutation.isPending || reportingDetailsMutation.isPending) return;
    setEditing(null);
    setDraft(emptyDraft);
    setImagePreview(null);
    setVariantLabelIsAutomatic(false);
    mutation.reset();
  }

  function updatePackageField(
    changes: Partial<Pick<ProductDraft, 'package_quantity' | 'measurement_unit' | 'packaging_type'>>,
  ) {
    setDraft((current) => {
      const nextDraft = { ...current, ...changes };
      return variantLabelIsAutomatic
        ? { ...nextDraft, variant_label: generatedVariantLabel(nextDraft) }
        : nextDraft;
    });
  }

  function updateVariantLabel(variant_label: string) {
    setDraft((current) => ({ ...current, variant_label }));
    setVariantLabelIsAutomatic(variant_label === generatedVariantLabel(draft));
  }

  function selectImage(file: File | null) {
    setDraft((current) => ({ ...current, image: file }));
    setImagePreview(file ? URL.createObjectURL(file) : null);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (editing !== 'new' && editing && hasReportingDetailsChange(editing, draft)) {
      reportingDetailsMutation.reset();
      setReportingDetailsReason('');
      setReportingDetailsProduct(editing);
      return;
    }
    mutation.mutate();
  }

  const priceMutation = useMutation({
    mutationFn: () => apiRequest<ProductPrice>(
      editingPrice ? `/product-prices/${editingPrice.id}/` : '/product-prices/',
      {
        ...(accessToken ? { accessToken } : {}),
        method: editingPrice ? 'PATCH' : 'POST',
        body: JSON.stringify({
          ...(editingPrice ? {} : { product: pricingProduct?.id }),
          venue: priceDraft.venue || null,
          price_type: priceDraft.price_type,
          amount: priceDraft.amount,
          starts_on: priceDraft.starts_on,
          ends_on: priceDraft.ends_on || null,
          is_active: priceDraft.is_active,
        }),
      },
    ),
    onSuccess: async () => {
      setEditingPrice(null);
      setPriceDraft(emptyPriceDraft);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-prices'] });
    },
  });

  function openPrices(product: Product) {
    setPricingProduct(product);
    setEditingPrice(null);
    setPriceDraft({ ...emptyPriceDraft, starts_on: new Date().toISOString().slice(0, 10) });
  }

  function closePrices() {
    if (priceMutation.isPending) return;
    setPricingProduct(null);
    setEditingPrice(null);
    setPriceDraft(emptyPriceDraft);
  }

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Products</h1>
          <p>Maintain reusable client-owned products for campaign targets and field sales.</p>
        </div>
        {!canManage ? (
          <span className="record-count"><PackageSearch size={16} /> {rows.length} products</span>
        ) : null}
      </header>

      <div className="product-list-toolbar">
        <label className="search-field">
          <Search aria-hidden="true" size={17} />
          <input
            aria-label="Search products"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search product, family, variant, SKU, category or client"
            type="search"
            value={search}
          />
        </label>
        {canManage ? (
          <div className="product-list-toolbar-actions">
            <button className="button secondary" onClick={() => setImporting(true)} type="button"><Upload size={17} /> Import</button>
            <button className="button secondary" disabled={exportMutation.isPending} onClick={() => exportMutation.mutate()} type="button"><Download size={17} /> Export</button>
            <button className="button primary" disabled={clientOptions.length === 0} onClick={beginCreate} type="button"><Plus size={17} /> Add product</button>
          </div>
        ) : null}
      </div>

      {importing ? <ProductImportPanel accessToken={accessToken} clients={clientOptions} initialClient={selectedWorkspace?.type === 'client' ? selectedWorkspace.organizationId ?? '' : clientOptions[0]?.id ?? ''} onClose={() => setImporting(false)} /> : null}

      {editing ? (
        <div
          className="confirmation-backdrop"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeForm(); }}
          role="presentation"
        >
        <section aria-labelledby="product-form-dialog-title" aria-modal="true" className="confirmation-dialog product-form-dialog" role="dialog">
          <button aria-label="Close product form" className="icon-button confirmation-close" disabled={mutation.isPending || reportingDetailsMutation.isPending} onClick={closeForm} type="button"><X size={19} /></button>
          <div className="card-heading product-form-dialog-heading">
            <div><h2 id="product-form-dialog-title">{editing === 'new' ? 'Add product' : `Edit ${editing.name}`}</h2><p>Products always remain owned by their original client.</p></div>
          </div>
          <form className="management-form" onSubmit={submit}>
            <div className="form-grid">
              <label>Client<select disabled={editing !== 'new'} required value={draft.client} onChange={(event) => setDraft({ ...draft, client: event.target.value, family: '', category: '' })}><option value="">Select client</option>{clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label>
              <label>Product family<select value={draft.family} onChange={(event) => setDraft({ ...draft, family: event.target.value })}><option value="">Ungrouped product</option>{familyOptions.map((family) => <option key={family.id} value={family.id}>{family.name}{family.brand_name ? ` · ${family.brand_name}` : ''}</option>)}</select></label>
              <label>Sellable product name<input placeholder="e.g. Pepsi Cola 250ml Can" required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
              <label>SKU<input placeholder="e.g. PEPSI-250-CAN" value={draft.sku} onChange={(event) => setDraft({ ...draft, sku: event.target.value })} /></label>
              <label>Category<select value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })}><option value="">Uncategorised product</option>{categoryOptions.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>
              <label>Packaging type<select value={draft.packaging_type} onChange={(event) => updatePackageField({ packaging_type: event.target.value })}><option value="">Not set</option>{['bottle', 'can', 'bag', 'packet', 'box', 'jar', 'tube', 'sachet', 'tin', 'carton', 'other'].map((value) => <option key={value} value={value}>{value.charAt(0).toUpperCase() + value.slice(1)}</option>)}</select></label>
              <label>Package quantity<input min="0.001" placeholder="e.g. 500 ml / 250 g" step="0.001" type="number" value={draft.package_quantity} onChange={(event) => updatePackageField({ package_quantity: event.target.value })} /></label>
              <label>Measurement unit<select value={draft.measurement_unit} onChange={(event) => updatePackageField({ measurement_unit: event.target.value })}><option value="">Not set</option><option value="piece">Piece</option><option value="g">Gram (g)</option><option value="kg">Kilogram (kg)</option><option value="ml">Millilitre (ml)</option><option value="l">Litre (L)</option></select><small className="form-help">Required for mass or volume targets.</small></label>
              <label>Variant label<input placeholder="e.g. 250 ml can" value={draft.variant_label} onChange={(event) => updateVariantLabel(event.target.value)} /><small className="form-help">Generated from package details until you customise it.</small></label>
              <label>Barcode / GTIN<input placeholder="e.g. 8961000000000" value={draft.barcode} onChange={(event) => setDraft({ ...draft, barcode: event.target.value })} /></label>
              <label>Units per carton / case<input min="1" placeholder="e.g. 24" step="1" type="number" value={draft.units_per_case} onChange={(event) => setDraft({ ...draft, units_per_case: event.target.value })} /></label>
              <label>Default unit price (PKR)<input min="0" step="0.01" type="number" value={draft.unit_price} onChange={(event) => setDraft({ ...draft, unit_price: event.target.value })} /><small className="form-help">Used only when no effective standard, promotional, or store price exists.</small></label>
              <div className="product-image-field wide-field">
                <ProductImage className="product-image-preview" name={draft.name || 'Product'} source={displayedImage} />
                <div>
                  <strong>Product image</strong>
                  <input
                    accept="image/*"
                    aria-label="Choose product image"
                    className="file-picker-input"
                    id="product-image"
                    onChange={(event) => selectImage(event.target.files?.[0] ?? null)}
                    type="file"
                  />
                  <label className="file-picker-button" htmlFor="product-image">Choose image</label>
                  <small>{draft.image?.name ?? mediaFileName(storedImage) ?? 'No image uploaded'}</small>
                </div>
              </div>
              <label className="checkbox-field"><input checked={Boolean(draft.theme_color)} type="checkbox" onChange={(event) => setDraft({ ...draft, theme_color: event.target.checked ? '#2F8065' : '' })} /> Use a custom chart colour</label>
              {draft.theme_color ? <label>SKU chart colour
                <span className="brand-theme-colour-inputs">
                  <input aria-label="SKU chart colour picker" type="color" value={/^#[0-9A-Fa-f]{6}$/.test(draft.theme_color) ? draft.theme_color : '#2F8065'} onChange={(event) => setDraft({ ...draft, theme_color: event.target.value.toUpperCase() })} />
                  <input aria-label="SKU chart colour" type="text" maxLength={7} pattern="#[0-9A-Fa-f]{6}" placeholder="#2F8065" required value={draft.theme_color} onChange={(event) => setDraft({ ...draft, theme_color: event.target.value.toUpperCase() })} />
                </span>
                <small className="brand-theme-colour-hint">Used for this SKU in Overview Product sales breakdown and sales-detail heatmaps.</small>
              </label> : <p className="form-help">Chart colour follows the brand/default palette. Enable a custom colour to distinguish this variant.</p>}
              <label className="checkbox-field"><input checked={draft.is_active} type="checkbox" onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })} /> Available for new campaigns</label>
            </div>
            {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
            <div className="form-actions">
              {editing !== 'new' ? <button className="button secondary" disabled={mutation.isPending || reportingDetailsMutation.isPending} onClick={() => { const product = editing; closeForm(); openPrices(product); }} type="button"><BadgeDollarSign size={17} /> Manage prices</button> : null}
              <button className="button secondary" disabled={mutation.isPending || reportingDetailsMutation.isPending} onClick={closeForm} type="button">Cancel</button>
              <button className="button primary" disabled={mutation.isPending || reportingDetailsMutation.isPending} type="submit">{mutation.isPending || reportingDetailsMutation.isPending ? <InlineSpinner /> : null}{mutation.isPending || reportingDetailsMutation.isPending ? 'Saving…' : 'Save product'}</button>
            </div>
          </form>
        </section>
        </div>
      ) : null}

      {reportingDetailsProduct ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !reportingDetailsMutation.isPending) { setReportingDetailsProduct(null); setReportingDetailsReason(''); reportingDetailsMutation.reset(); } }} role="presentation">
          <section aria-labelledby="product-reporting-details-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <button aria-label="Close reporting details confirmation" className="icon-button confirmation-close" disabled={reportingDetailsMutation.isPending} onClick={() => { setReportingDetailsProduct(null); setReportingDetailsReason(''); reportingDetailsMutation.reset(); }} type="button"><X size={19} /></button>
            <h2 id="product-reporting-details-title">Apply reporting details change</h2>
            <p><strong>{reportingDetailsProduct.name}</strong> has changes to its product family, package quantity, or measurement unit. Record why this change is needed, then choose whether existing sales should be corrected.</p>
            <label>Reason for change<textarea aria-label="Reason for reporting details change" maxLength={500} onChange={(event) => setReportingDetailsReason(event.target.value)} placeholder="e.g. Product family was missed during catalogue setup." required rows={3} value={reportingDetailsReason} /></label>
            <p className="confirmation-note"><strong>Correct historical sales</strong> updates the selected details on all past sales snapshots. <strong>Update only future sales</strong> keeps every historical snapshot unchanged and uses the new details for future sales.</p>
            {reportingDetailsMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(reportingDetailsMutation.error)}</p> : null}
            <div className="confirmation-actions"><button className="button secondary" disabled={reportingDetailsMutation.isPending} onClick={() => { setReportingDetailsProduct(null); setReportingDetailsReason(''); reportingDetailsMutation.reset(); }} type="button">Cancel</button><button className="button secondary" disabled={reportingDetailsMutation.isPending || !reportingDetailsReason.trim()} onClick={() => reportingDetailsMutation.mutate('future')} type="button">{reportingDetailsMutation.isPending ? <InlineSpinner /> : null}Update only future sales</button><button className="button primary" disabled={reportingDetailsMutation.isPending || !reportingDetailsReason.trim()} onClick={() => reportingDetailsMutation.mutate('historical')} type="button">{reportingDetailsMutation.isPending ? <InlineSpinner /> : null}Correct historical sales</button></div>
          </section>
        </div>
      ) : null}

      {pricingProduct ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closePrices(); }} role="presentation">
          <section aria-labelledby="product-price-dialog-title" aria-modal="true" className="confirmation-dialog product-family-dialog" role="dialog">
            <button aria-label="Close product prices" className="icon-button confirmation-close" disabled={priceMutation.isPending} onClick={closePrices} type="button"><X size={19} /></button>
            <div className="card-heading product-family-dialog-heading"><div><h2 id="product-price-dialog-title">Prices for {pricingProduct.name}</h2><p>Store-specific prices override global prices; promotions override standard prices within the same scope.</p></div></div>
            <form className="management-form" onSubmit={(event) => { event.preventDefault(); priceMutation.mutate(); }}>
              <div className="form-grid">
                <label>Store scope<StorePicker allOptionLabel="All stores" ariaLabel="Store scope" onChange={(venue) => setPriceDraft({ ...priceDraft, venue })} options={(venues.data?.results ?? []).map((venue) => ({ id: venue.id, name: venue.name, address: venue.address, city: venue.city, chainName: venue.chain_name, storeCode: venue.store_code }))} value={priceDraft.venue} /></label>
                <label>Price type<select value={priceDraft.price_type} onChange={(event) => setPriceDraft({ ...priceDraft, price_type: event.target.value as ProductPriceDraft['price_type'] })}><option value="standard">Standard</option><option value="promotional">Promotional</option></select></label>
                <label>Price (PKR)<input min="0" required step="0.01" type="number" value={priceDraft.amount} onChange={(event) => setPriceDraft({ ...priceDraft, amount: event.target.value })} /></label>
                <label>Effective from<input required type="date" value={priceDraft.starts_on} onChange={(event) => setPriceDraft({ ...priceDraft, starts_on: event.target.value })} /></label>
                <label>Effective until<input min={priceDraft.starts_on} type="date" value={priceDraft.ends_on} onChange={(event) => setPriceDraft({ ...priceDraft, ends_on: event.target.value })} /></label>
                <label className="checkbox-field"><input checked={priceDraft.is_active} type="checkbox" onChange={(event) => setPriceDraft({ ...priceDraft, is_active: event.target.checked })} /> Active price</label>
              </div>
              {priceMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(priceMutation.error)}</p> : null}
              <div className="form-actions">{editingPrice ? <button className="button secondary" onClick={() => { setEditingPrice(null); setPriceDraft({ ...emptyPriceDraft, starts_on: new Date().toISOString().slice(0, 10) }); }} type="button">Cancel edit</button> : null}<button className="button primary" disabled={priceMutation.isPending} type="submit">{priceMutation.isPending ? <InlineSpinner /> : null}{editingPrice ? 'Save price' : 'Add price'}</button></div>
            </form>
            <div className="product-family-list"><div className="data-table-wrap"><table className="data-table"><thead><tr><th>Scope</th><th>Type</th><th>Price</th><th>Effective dates</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead><tbody>{(prices.data?.results ?? []).map((price) => <tr key={price.id}><td>{price.venue_name || 'All stores'}</td><td><StatusPill value={price.price_type} /></td><td>{priceFormatter.format(Number(price.amount))}</td><td>{price.starts_on} – {price.ends_on || 'Open ended'}</td><td><StatusPill value={price.is_active ? 'active' : 'inactive'} /></td><td><button className="text-button" onClick={() => { setEditingPrice(price); setPriceDraft({ venue: price.venue ?? '', price_type: price.price_type, amount: price.amount, starts_on: price.starts_on, ends_on: price.ends_on ?? '', is_active: price.is_active }); }} type="button"><Pencil size={14} /> Edit</button></td></tr>)}</tbody></table></div></div>
          </section>
        </div>
      ) : null}

      <DataPanel loading={products.isLoading || relationships.isLoading} error={products.isError || relationships.isError} empty={rows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Sellable product</th><th>Family</th><th>Client</th><th>Category</th><th>Unit price</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{rows.map((product) => <tr key={product.id}>
              <td><div className="product-table-identity"><ProductImage className="product-image-thumbnail" name={product.name} source={product.image} /><span><strong>{product.name}</strong><small>{[product.variant_label, product.sku || 'No SKU', product.barcode || null].filter(Boolean).join(' · ')}</small>{product.theme_color ? <small className="brand-theme-colour"><i aria-hidden="true" style={{ backgroundColor: product.theme_color }} />Chart colour: {product.theme_color}</small> : null}</span></div></td>
              <td><strong>{product.family_name || 'Ungrouped'}</strong>{product.family_brand ? <small>{product.family_brand}</small> : null}</td>
              <td>{product.client_name}</td>
              <td>{product.category_name || '—'}</td>
              <td>{product.unit_price ? priceFormatter.format(Number(product.unit_price)) : '—'}</td>
              <td><StatusPill value={product.is_active ? 'active' : 'inactive'} /></td>
              <td><div className="table-actions">{canManage && (isPlatformAdmin || product.client === selectedWorkspace?.organizationId) ? <button className="text-button" onClick={() => beginEdit(product)} type="button"><Pencil size={14} /> Edit</button> : null}</div></td>
            </tr>)}</tbody>
          </table></div>
        </section>
      </DataPanel>
    </>
  );
}
